import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Ban, FileText, Mail, MailOpen, MousePointerClick, Send, UserMinus } from 'lucide-react';
import { useChannelQuotas } from '@/features/channel-quota/model/use-channel-quotas';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { KpiCard } from '@/shared/components/kpi-card';
import { formatDate, formatTime, percent } from '@/shared/lib/format';
import { usePassbook } from '@/shared/lib/passbook';
import { Badge } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Pagination } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { BackToCampaigns, CampaignActions } from './campaign-actions';
import { EmailPreview } from './email-preview';
import {
  AudienceSummary,
  CAMPAIGN_STATUS_META,
  ChannelErrorState,
  ChannelLoading,
  ConsumptionPanel,
  formatCount,
  RECIPIENT_STATUS_META,
  type RecipientStatus,
} from '@/features/marketing-shared';
import { useEmailMarketing } from '../model/context';
import { RECIPIENTS_PAGE_SIZE, useEmailCampaignDetail } from '../model/use-email-campaign-detail';
import type { EmailCampaign } from '../model/types';

const RECIPIENT_FILTERS: Array<{ value: 'all' | RecipientStatus; label: string }> = [
  { value: 'all', label: 'Todos' },
  ...(Object.keys(RECIPIENT_STATUS_META) as RecipientStatus[]).map((status) => ({
    value: status,
    label: RECIPIENT_STATUS_META[status].label,
  })),
];

function whenLabel(campaign: EmailCampaign): string {
  if (!campaign.scheduledAt) return 'Sem data de envio';
  if (campaign.sendMode === 'now') return `Na aprovação · ${formatDate(campaign.scheduledAt)} ${formatTime(campaign.scheduledAt)}`;
  return `${formatDate(campaign.scheduledAt)} ${formatTime(campaign.scheduledAt)}`;
}

export function EmailCampaignDetailPage() {
  const { campaignId } = useParams();
  const { idPassbook, passbook } = usePassbook();
  const { templates } = useEmailMarketing();
  const { quotas } = useChannelQuotas(idPassbook);
  const [page, setPage] = useState(1);
  const { campaign, state, detailPending } = useEmailCampaignDetail(campaignId, page);
  const [recipientStatus, setRecipientStatus] = useState<'all' | RecipientStatus>('all');
  const [query, setQuery] = useState('');
  const storeName = passbook.organizationName || 'Sua loja';

  useEffect(() => {
    setPage(1);
    setRecipientStatus('all');
    setQuery('');
  }, [campaignId]);

  const recipientsPage = campaign?.recipients;
  const recipients = useMemo(() => {
    const rows = recipientsPage?.rows ?? [];
    const term = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (recipientStatus !== 'all' && row.status !== recipientStatus) return false;
      if (!term) return true;
      return row.name.toLowerCase().includes(term) || row.reason.toLowerCase().includes(term);
    });
  }, [recipientsPage, query, recipientStatus]);

  if (!campaign && state.phase === 'loading') {
    return (
      <>
        <PageHeader title="Campanha" leading={<BackToCampaigns />} />
        <ChannelLoading label="Carregando a campanha" />
      </>
    );
  }

  if (!campaign && state.phase === 'error' && state.error?.kind !== 'not_found') {
    return (
      <>
        <PageHeader title="Campanha" leading={<BackToCampaigns />} />
        <ChannelErrorState title="Não foi possível carregar a campanha" error={state.error} onRetry={state.refetch} />
      </>
    );
  }

  if (!campaign) {
    return (
      <>
        <PageHeader title="Campanha não encontrada" leading={<BackToCampaigns />} />
        <Card>
          <EmptyState
            icon={FileText}
            title="Campanha não encontrada"
            description="Esse identificador não existe nesta loja ou a campanha foi excluída."
          />
        </Card>
      </>
    );
  }

  const meta = CAMPAIGN_STATUS_META[campaign.status];
  const metrics = campaign.metrics;
  // Só o rascunho confere o saldo: agendada e pausada já reservaram na
  // aprovação, e o saldo que sobrou já desconta a própria campanha.
  const showEstimate = campaign.status === 'draft';
  const maxSent = Math.max(...(campaign.evolution?.map((point) => point.sent) ?? [1]), 1);
  const template = templates.find((item) => item.id === campaign.templateId);
  const capacity = quotas?.email;
  const detailFailed = state.phase === 'error' && state.error?.kind !== 'not_found';

  return (
    <div className="min-w-0">
      <PageHeader
        title={campaign.name}
        description={`${meta.label} · ${whenLabel(campaign)}`}
        leading={<BackToCampaigns />}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <CampaignActions campaign={campaign} />
          </div>
        }
      />

      <div className="mb-4">
        <AudienceSummary audience={campaign.audience} headingId="email-publico" tone="kpi" />
      </div>

      {campaign.failureSummary ? (
        <p className="mb-4 rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm" role="status">
          {campaign.failureSummary}
        </p>
      ) : null}

      {detailFailed ? (
        <div className="mb-4">
          <ChannelErrorState
            title="Resultados e destinatários não carregaram"
            error={state.error}
            onRetry={state.refetch}
          />
        </div>
      ) : null}

      {metrics ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <KpiCard label="Enviados" value={formatCount(metrics.sent)} icon={Send} index={0} />
          <KpiCard
            label="Entregues"
            value={formatCount(metrics.delivered)}
            icon={Mail}
            hint={`${percent(metrics.delivered, metrics.sent)} dos enviados`}
            index={1}
          />
          <KpiCard
            label="Abertos"
            value={formatCount(metrics.opened)}
            icon={MailOpen}
            hint={`${percent(metrics.opened, metrics.delivered)} dos entregues`}
            index={2}
          />
          <KpiCard
            label="Cliques"
            value={formatCount(metrics.clicked)}
            icon={MousePointerClick}
            hint={`${percent(metrics.clicked, metrics.opened)} dos abertos`}
            index={3}
          />
          <KpiCard label="Rejeitados" value={formatCount(metrics.rejected)} icon={Ban} index={4} />
          <KpiCard label="Descadastros" value={formatCount(metrics.unsubscribed)} icon={UserMinus} index={5} />
        </div>
      ) : detailPending && !detailFailed ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : (
        <Card className="mb-4">
          <EmptyState
            icon={Send}
            title="Ainda não há resultados de envio"
            description="Os números aparecem quando o primeiro lote é aceito pelo provedor e os eventos de entrega começam a chegar."
          />
        </Card>
      )}

      {campaign.evolution && campaign.evolution.length > 0 ? (
        <Card className="mb-4 p-4 sm:p-5">
          <h2 className="text-sm font-medium">Evolução do envio</h2>
          <p className="mt-1 text-xs text-muted-foreground">Acumulado por hora, a partir dos eventos do provedor.</p>
          <ol className="mt-4 space-y-3">
            {campaign.evolution.map((point) => (
              <li key={point.label}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span>{point.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatCount(point.sent)} enviados · {formatCount(point.delivered)} entregues
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.max(4, (point.delivered / maxSent) * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {campaign.errors && campaign.errors.length > 0 ? (
        <Card className="mb-4 min-w-0 p-4 sm:p-5">
          <h2 className="mb-3 text-sm font-medium">Erros</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Mensagem</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaign.errors.map((error) => (
                <TableRow key={error.id}>
                  <TableCell className="whitespace-nowrap tabular-nums">{error.code}</TableCell>
                  <TableCell>{error.message}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCount(error.count)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      {recipientsPage && recipientsPage.total > 0 ? (
        <Card className="mb-4 min-w-0 p-4 sm:p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-medium">Destinatários</h2>
            <p className="text-xs tabular-nums text-muted-foreground">{formatCount(recipientsPage.total)} no total</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground sm:hidden">
            Deslize para o lado para ver e-mail, data e dispositivo.
          </p>
          <div className="mt-3 grid gap-2 sm:flex sm:flex-wrap">
            <div>
              <Label htmlFor="estado-destinatario" className="sr-only">
                Filtrar destinatários
              </Label>
              <Select
                value={recipientStatus}
                onValueChange={(value) => setRecipientStatus(value as 'all' | RecipientStatus)}
              >
                <SelectTrigger id="estado-destinatario" className="w-full sm:w-auto sm:min-w-[10rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECIPIENT_FILTERS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 sm:w-56">
              <Label htmlFor="busca-destinatario" className="sr-only">
                Buscar destinatário
              </Label>
              <Input
                id="busca-destinatario"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por nome ou motivo"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            O filtro vale para a página carregada ({formatCount(recipientsPage.rows.length)} de {formatCount(recipientsPage.total)}).
          </p>
          <div className="mt-3" aria-busy={detailPending ? 'true' : undefined}>
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background">Nome</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Quando</TableHead>
                  <TableHead>Dispositivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Nenhum destinatário neste filtro.
                    </TableCell>
                  </TableRow>
                ) : (
                  recipients.map((row) => {
                    const rowMeta = RECIPIENT_STATUS_META[row.status];
                    return (
                      <TableRow key={row.id} className="group">
                        <TableCell className="sticky left-0 z-10 bg-background font-medium group-hover:bg-muted">
                          {row.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rowMeta.variant}>{rowMeta.label}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[16rem]">{row.reason || '—'}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">{row.emailMasked}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {row.at ? `${formatDate(row.at)} ${formatTime(row.at)}` : '—'}
                        </TableCell>
                        <TableCell>{row.device}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {recipientsPage.total > recipientsPage.pageSize ? (
            <Pagination
              page={recipientsPage.page - 1}
              pageSize={recipientsPage.pageSize || RECIPIENTS_PAGE_SIZE}
              total={recipientsPage.total}
              onPageChange={(next) => setPage(next + 1)}
              className="justify-center pt-3"
            />
          ) : null}
        </Card>
      ) : null}

      <div className="mb-4">
        {showEstimate ? (
          capacity ? (
            <ConsumptionPanel eligible={campaign.audience.eligible} capacity={capacity} channel="email" />
          ) : (
            <p className="text-sm text-muted-foreground">
              Volume previsto: {formatCount(campaign.audience.eligible)} e-mails. O saldo da loja ainda não respondeu.
            </p>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            Na aprovação, o volume previsto era {formatCount(campaign.audience.eligible)} e-mails; os slots foram
            reservados no saldo da loja naquele momento.
          </p>
        )}
      </div>

      <Card className="p-4 sm:p-5">
        <h2 className="mb-3 text-sm font-medium">Conteúdo</h2>
        <dl className="mb-3 space-y-2 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-muted-foreground">Remetente</dt>
            <dd className="min-w-0 break-all text-right font-medium">
              {campaign.fromName} &lt;{campaign.fromEmail}&gt;
            </dd>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-muted-foreground">Template</dt>
            <dd className="min-w-0 break-words text-right font-medium">{campaign.templateName || '—'}</dd>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-muted-foreground">Tags</dt>
            <dd className="min-w-0 break-words text-right font-medium">{campaign.tags.length ? campaign.tags.join(', ') : 'Nenhuma'}</dd>
          </div>
        </dl>
        <EmailPreview
          subject={campaign.subject}
          previewText={campaign.previewText}
          blocks={campaign.blocks}
          storeName={storeName}
          fromName={campaign.fromName}
          fromEmail={campaign.fromEmail}
          editor={template?.editor}
          html={template?.html}
        />
      </Card>
    </div>
  );
}
